import {
  Box,
  CardContent,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import DashboardTimeRange from '../DashboardTimeRange';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useEffect, useState } from 'react';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { useApi } from '@bumble/app/hooks/use-api';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getTooltipLabel } from '@bumble/app/utils/get-date';
import PathVisualization from './PathVisualization';
import DataTableGrid from '../DataTableGrid';
import { GridColDef } from '@mui/x-data-grid';
import { apiError } from '@bumble/app/utils/api-error';
import { useCpe } from './CpeContext';

const columns: GridColDef[] = [
  {
    field: '@timestamp',
    headerName: 'Timestamp',
    width: 300,
    disableColumnMenu: true,
  }, {
    field: 'status',
    headerName: 'Status',
    width: 200,
    disableColumnMenu: true,
  }, {
    field: 'gw_pingable',
    headerName: 'Self Diagnosis',
    flex: 1,
    minWidth: 300,
    disableColumnMenu: true,
  }
];

interface StatusDataRow {
  node_id: string;
  '@timestamp': string;
  gw_pingable: string;
  status: string;
}

interface WANLinkHealthDashboardProps {
  selectedTab: string;
}

export const WANLinkHealthDashboard = ({ selectedTab }: WANLinkHealthDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const { cpe, setCpe } = useCpe();
  const [healthChardData, setHealthChartData] = useState<any[]>([]);
  const [rttChartData, setRttChartData] = useState<any[]>([]);
  const [lossChartData, setLossChartData] = useState<any[]>([]);
  const [upTimePercent, setUpTimePercent] = useState<string>('');
  const [statusTableData, setStatusTableData] = useState<any[]>([]);
  const [speedTestData, setSpeedTestData] = useState<any[]>([]);
  const [isLoading1, setIsLoading1] = useState<boolean>(false);
  const [tracerouteData, setTracerouteData] = useState<{ hostname: string; ip: string }[]>([]);

  const api = useApi();
  const colors = ['#6E9FCE', '#C5904A', '#5F467E'];

  const apiCall = useMutation(api.dashboardGetLinkSpeedTestApiV1DashboardGetLinkSpeedTestGet, {
    onSuccess(data) {
      const fullData = (data.data as any).link_speed_test;
      setIsLoading1(false);
      setSpeedTestData(fullData);
    },
    onError(e) {
      setIsLoading1(false);
      apiError(e);
      setSpeedTestData([]);
    }
  })

  const { isLoading, data, error, refetch, fetchStatus, isRefetching } = useQuery(
    ['graphdata'],
    api.dashboardGetNodeWanLinkHealthApiV1DashboardGetNodeWanLinkHealthGet.bind(null, {
      node_id: cpe.nodeId,
      node_name: cpe.cpeName,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false, onError: apiError },
  );

  const {
    isLoading: statusLoading,
    data: statusData,
    error: statusError,
    refetch: statusRefetch,
    fetchStatus: statusFetchStatus,
    isRefetching: statusRefetching
  } = useQuery(
    ['statusdata'],
    api.getNodeConnStatusApiV1GetNodeConnStatusGet.bind(null, { node_id: cpe.nodeId }),
    { enabled: false, retry: false, onError: apiError },
  );

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) {
      setTimeRange(val);
    }
  };

  const handleCpeChange = (val: any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id,
      });
    }
  };

  useEffect(() => {
    if (cpe.cpeName && timeRange) {
      setStatusTableData([]);
      setUpTimePercent('');
      refetch();
      statusRefetch();
    }
  }, [timeRange, cpe.cpeName])

  useEffect(() => {
    if (cpe.cpeName) {
      setIsLoading1(true);
      apiCall.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      })
    }
  }, [cpe.cpeName])

  useEffect(() => {
    if (data?.data && !error) {
      setHealthChartData(data.data.map(n => ({
        timestamp: n.timestamp,
        health: n.health,
      })))
      setRttChartData(data.data.map(n => ({
        timestamp: n.timestamp,
        rtt_ms: n.RTT_ms,
      })))
      setLossChartData(data.data.map(n => ({
        timestamp: n.timestamp,
        loss_rate: n['Loss Rate'],
      })))
    }
  }, [data])

  useEffect(() => {
    if (statusData?.data && !statusError) {
      setUpTimePercent(`${statusData.data.percent}`);
      setStatusTableData((statusData.data.history as StatusDataRow[]).map(h => {
        const origVal = h.gw_pingable.toLowerCase();
        const gw_pingable = h.status === 'up'
          ? 'Normal'
          : (origVal === 'yes'
              ? 'Last mile problem'
              : (origVal === 'no' ? 'Middle mile problem' : 'Undetermined')
            );
        return {
          ...h,
          id: `${h.node_id}-${h['@timestamp']}`,
          gw_pingable,
        }
      }));
    }
  }, [statusData, statusError])

  const handleRefreshClick = () => {
    if (cpe.cpeName) {
      setIsLoading1(true);
      apiCall.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      })
    }
    refetch();
    toast.success('Refreshed chart data.');
  };

  function CustomizedTicks(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date?.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  function CustomizedTicksForChartData(props: any) {
    const { x, y, payload } = props;
    const date = DateTime.fromISO(payload.value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  return (
    <Box>
      {upTimePercent ? (
        <>
          <Typography sx={{
            position: 'absolute',
            top: 182,
            left: 720,
            maxWidth: 200,
          }}>
            Uptime{' '}
          </Typography>
          <Typography sx={{
            position: 'absolute',
            top: 170,
            left: 780,
            maxWidth: 200,
          }}>
            <span style={{
              color: 'rgb(31, 207, 57)',
              fontSize: '2em',
              fontWeight: 'bold',
            }}>
              {upTimePercent}
            </span>
            <span style={{ color: 'rgb(31, 207, 57)' }}>%</span>
          </Typography>
        </>
      ) : ''}
      <Box sx={{ mb: 0, py: 5 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>WAN Link Health (%)</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ?
              <Loading height={200} /> :
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                  data={healthChardData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="category"
                    allowDuplicatedCategory={false}
                    height={40}
                    interval={7}
                    tick={<CustomizedTicks />}
                  />
                  <YAxis
                    orientation="left"
                    type="number"
                    domain={[0, 100]}
                    width={30}
                    tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      compactDisplay: 'short',
                    }).format(value)}
                  />
                  <Line strokeWidth={2} stroke={colors[0]} dot={false} dataKey="health" name="WAN Link Health" />
                  <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                </LineChart>
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <Grid container spacing={5} mb={5}>
        <Grid item sm={12} md={6}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
            <Typography color="primary" sx={{ mb: 2 }}>WAN Link RTT (ms)</Typography>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                data={rttChartData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  type="category"
                  allowDuplicatedCategory={false}
                  height={40}
                  interval={7}
                  tick={<CustomizedTicks />}
                />
                <YAxis
                  orientation="left"
                  type="number"
                  domain={rttChartData.length > 0 ? undefined : [0, 10]}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <Line strokeWidth={2} stroke={colors[1]} dot={false} dataKey="rtt_ms" name="WAN Link Round Trip Time" />
                <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              </LineChart>
            </ResponsiveContainer>}
          </CardContent>
        </Grid>
        <Grid item sm={12} md={6}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
            <Typography color="primary" sx={{ mb: 2 }}>WAN Link Packet Loss Rate (%)</Typography>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                data={lossChartData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  type="category"
                  allowDuplicatedCategory={false}
                  height={40}
                  interval={7}
                  tick={<CustomizedTicks />}
                />
                <YAxis
                  orientation="left"
                  type="number"
                  domain={[0, 100]}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey="loss_rate" name="WAN Link Packet Loss Rate" />
                <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              </LineChart>
            </ResponsiveContainer>}
          </CardContent>
        </Grid>
      </Grid>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>Path Visualization</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
              <ResponsiveContainer width="100%" height={200}>
                <PathVisualization
                  nodeId={cpe.nodeId}
                  nodeName={cpe.cpeName}
                  tracerouteData={setTracerouteData}
                />
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>Outage History</Typography>
              {((statusLoading || statusRefetching) && statusFetchStatus !== 'idle' && !statusError) ?
                <Loading height={300} /> :
                <Box sx={{ height: 300, overflow: 'auto' }}>
                  <DataTableGrid
                    rows={statusTableData}
                    columns={columns}
                    pagination={true}
                  />
                </Box>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 5 }}>Daily Performance Assurance Test (Mbps)</Typography>
              {isLoading1 ? <Loading /> :
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  margin={{ top: 5, right: 10, left: 20, bottom: 5, }}
                  data={speedTestData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="category"
                    allowDuplicatedCategory={false}
                    height={60}
                    interval={0}
                    tick={<CustomizedTicksForChartData />}
                  />
                  <YAxis
                    orientation="left"
                    type="number"
                    domain={[0, 1000]}
                    width={30}
                    tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      compactDisplay: 'short',
                    }).format(value)}`}
                  />
                  <Line strokeWidth={2} stroke={'#ECC052'} dot={false} dataKey="tx.mbps" name="Upload" />
                  <Line strokeWidth={2} stroke={'#5F467E'} dot={false} dataKey="rx.mbps" name="Download" />
                  <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                  <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
                </LineChart>
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Box>
  );
};
